/**
 * SeatsPanel — per-seat logins Phase 7. Lists, provisions and revokes Clerk seats through
 * the Phase 5 API (GET/POST/DELETE /api/v1/admin/seats). Mounted in /admin/users only when
 * REACT_APP_SEAT_ROLE_SOT is on (AdminUsersPage). The backend is the authority: every guard
 * (admin-only, owner-only admin seat, firm org_type, self, owner protection) is enforced
 * there; this panel only surfaces the result. A 404 means SEAT_PROVISIONING_ENABLED is off.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, KeyRound, AlertTriangle, Check } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  PROVISIONABLE_SEATS,
  ProvisionableSeatId,
  seatDisplayName,
} from '../../config/seatRoles';

export interface SeatRow {
  clerk_user_id: string;
  email?: string | null;
  fe_role?: string | null;
  be_role?: string | null;
  be_role_display?: string | null;
  tenant_org_id_metadata?: string | null;
  tenant_org_id_db?: string | null;
  firm_memberships?: Array<{ law_firm_id?: string; clerk_org_id?: string; org_type?: string; org_role?: string }>;
  mismatch?: boolean;
  reasons?: string[];
}

interface SeatsResponse {
  seats: SeatRow[];
  unbound_rows?: unknown[];
  limit?: number;
  offset?: number;
}

interface FirmOption {
  id: string;
  name: string;
  org_type?: 'firm' | 'mediator' | null;
}

const PAGE_LIMIT = 100;

const inputStyle = {
  background: 'var(--color-bg-elevated)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-primary)',
} as const;

function errMessage(e: any, fallback: string): string {
  return (typeof e?.message === 'string' && e.message) || fallback;
}

export default function SeatsPanel() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<SeatRow[]>([]);
  const [unbound, setUnbound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [firms, setFirms] = useState<FirmOption[]>([]);
  const [firmsError, setFirmsError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [seat, setSeat] = useState<ProvisionableSeatId>('accountant_internal');
  const [firmId, setFirmId] = useState('');
  const [sendInvite, setSendInvite] = useState(false);
  const [busy, setBusy] = useState(false);

  // getToken's identity is not guaranteed stable across renders; a ref keeps the loaders
  // from re-firing (and re-fetching in a loop) on every render.
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const headers = useCallback(async () => ({ Authorization: `Bearer ${await tokenRef.current()}` }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setError(null);
    try {
      const data = await api.get<SeatsResponse>(`/api/v1/admin/seats?limit=${PAGE_LIMIT}&offset=0`, {
        headers: await headers(),
        showErrorToast: false,
      });
      setRows(Array.isArray(data?.seats) ? data.seats : []);
      setUnbound(Array.isArray(data?.unbound_rows) ? data.unbound_rows.length : 0);
      setDisabled(false);
    } catch (e: any) {
      if (e?.status === 404) setDisabled(true);
      else setLoadError(errMessage(e, 'Could not load seats.'));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await api.get<{ law_firms?: FirmOption[] }>('/api/v1/law-firms', {
          headers: await headers(),
          showErrorToast: false,
        });
        if (active) setFirms(Array.isArray(data?.law_firms) ? data.law_firms : []);
      } catch (e: any) {
        if (active) setFirmsError(errMessage(e, 'Could not load firms.'));
      }
    })();
    return () => {
      active = false;
    };
  }, [headers]);

  const spec = PROVISIONABLE_SEATS.find((s) => s.id === seat) ?? PROVISIONABLE_SEATS[0];
  const firmChoices = useMemo(
    () => (spec.orgType ? firms.filter((f) => (f.org_type || 'firm') === spec.orgType) : []),
    [firms, spec.orgType],
  );

  const onSeatChange = (next: ProvisionableSeatId) => {
    setSeat(next);
    setFirmId('');
  };

  const onProvision = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr || !addr.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    if (spec.orgType && !firmId) {
      setError('Choose a firm for this seat.');
      return;
    }
    if (sendInvite && !window.confirm(`Clerk will email an invitation to ${addr}. Continue?`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body: Record<string, unknown> = { email: addr, seat, send_invite: sendInvite };
      if (spec.orgType) body.firm_id = firmId;
      const res = await api.post<{ path?: string }>('/api/v1/admin/seats', body, {
        headers: await headers(),
        showErrorToast: false,
      });
      setNotice(`${seatDisplayName(seat)} seat provisioned for ${addr} (${res?.path || 'ok'}).`);
      setEmail('');
      await load();
    } catch (e: any) {
      setError(errMessage(e, 'Provisioning failed.'));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (row: SeatRow) => {
    const seatLabel = row.fe_role ? ` ${seatDisplayName(row.fe_role)}` : '';
    if (!window.confirm(`Revoke the${seatLabel} seat for ${row.email || row.clerk_user_id}?`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/api/v1/admin/seats/${encodeURIComponent(row.clerk_user_id)}`, undefined, {
        headers: await headers(),
        showErrorToast: false,
      });
      setNotice(`Seat revoked for ${row.email || row.clerk_user_id}.`);
      await load();
    } catch (e: any) {
      setError(errMessage(e, 'Revoke failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seats-panel">
      <div className="flex items-center gap-3">
        <KeyRound className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        <div>
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Seats</h2>
          <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
            Provision and revoke real logins. The role is written to Clerk and mirrored to the backend.
          </p>
        </div>
      </div>

      <div className="rounded-xl p-6 space-y-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : loadError ? (
          <div
            role="alert"
            className="rounded-lg px-4 py-3 text-sm flex items-center justify-between"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-needs-human-text)', color: 'var(--color-needs-human-text)' }}
          >
            <span>{loadError}</span>
            <button
              type="button"
              onClick={() => { setLoadError(null); void load(); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono uppercase tracking-wide whitespace-nowrap border transition-colors cursor-pointer hover:text-[var(--color-needs-human-text)] hover:border-[var(--color-needs-human-text)]"
              style={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
            >
              Retry
            </button>
          </div>
        ) : disabled ? (
          <p className="cs-body" data-testid="seats-disabled" style={{ color: 'var(--color-text-muted)' }}>
            Seat provisioning is not enabled on this backend (SEAT_PROVISIONING_ENABLED is off).
          </p>
        ) : (
          <>
            {notice && (
              <div role="status" className="rounded-lg px-4 py-2 text-sm" style={{ border: '1px solid var(--color-verified)', color: 'var(--color-verified)' }}>
                {notice}
              </div>
            )}
            {error && (
              <div role="alert" className="rounded-lg px-4 py-2 text-sm" style={{ border: '1px solid var(--color-needs-human-text)', color: 'var(--color-needs-human-text)' }}>
                {error}
              </div>
            )}

            <form onSubmit={onProvision} className="flex flex-wrap items-end gap-3" aria-label="Provision a seat" data-testid="seat-form">
              <div>
                <label htmlFor="seat-email" className="cs-label">Email</label>
                <input
                  id="seat-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block py-2 px-3 rounded-lg text-sm w-64 max-w-full"
                  style={inputStyle}
                />
              </div>
              <div>
                <label htmlFor="seat-seat" className="cs-label">Seat</label>
                <select
                  id="seat-seat"
                  value={seat}
                  onChange={(e) => onSeatChange(e.target.value as ProvisionableSeatId)}
                  className="mt-1 block py-2 px-3 rounded-lg text-sm"
                  style={inputStyle}
                >
                  {PROVISIONABLE_SEATS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {seatDisplayName(s.id)}{s.ownerOnly ? ' (owner only)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              {spec.orgType && (
                <div>
                  <label htmlFor="seat-firm" className="cs-label">{spec.orgType === 'mediator' ? 'Mediation company' : 'Law firm'}</label>
                  <select
                    id="seat-firm"
                    value={firmId}
                    onChange={(e) => setFirmId(e.target.value)}
                    disabled={!!firmsError}
                    className="mt-1 block py-2 px-3 rounded-lg text-sm"
                    style={inputStyle}
                  >
                    <option value="">Choose…</option>
                    {firmChoices.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2 cs-body">
                <input id="seat-invite" type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />
                <label htmlFor="seat-invite">Send Clerk invitation email</label>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                Provision seat
              </button>
            </form>
            {spec.orgType && firmsError && (
              <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>{firmsError}</p>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th className="text-left font-mono text-[11px] font-normal uppercase tracking-wide py-3 pr-4" style={{ color: 'var(--color-text-dim)' }}>User</th>
                    <th className="text-left font-mono text-[11px] font-normal uppercase tracking-wide py-3 pr-4" style={{ color: 'var(--color-text-dim)' }}>Seat (Clerk)</th>
                    <th className="text-left font-mono text-[11px] font-normal uppercase tracking-wide py-3 pr-4" style={{ color: 'var(--color-text-dim)' }}>Backend role</th>
                    <th className="text-left font-mono text-[11px] font-normal uppercase tracking-wide py-3 pr-4" style={{ color: 'var(--color-text-dim)' }}>Status</th>
                    <th className="py-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center cs-body" style={{ color: 'var(--color-text-muted)' }}>No seats yet.</td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.clerk_user_id} data-testid="seat-row" style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                      <td className="py-3 pr-4 truncate max-w-[160px] xl:max-w-[240px]" title={r.email || r.clerk_user_id} style={{ color: 'var(--color-text-primary)' }}>
                        {r.email || r.clerk_user_id}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                        {r.fe_role ? seatDisplayName(r.fe_role) : <span style={{ color: 'var(--color-text-ghost)' }}>—</span>}
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                        {r.be_role_display || r.be_role ? (r.be_role_display || r.be_role) : <span style={{ color: 'var(--color-text-ghost)' }}>—</span>}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {r.mismatch ? (
                          <span
                            data-testid="seat-mismatch"
                            title={(r.reasons || []).join(', ')}
                            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide whitespace-nowrap"
                            style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            FE/BE mismatch
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-mono uppercase whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>
                            <Check className="w-3 h-3" />
                            In sync
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right whitespace-nowrap">
                        {(r.fe_role || (r.firm_memberships?.length ?? 0) > 0) && (
                          <button
                            type="button"
                            onClick={() => void onRevoke(r)}
                            disabled={busy}
                            className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-mono uppercase tracking-wide whitespace-nowrap border transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:text-[var(--color-needs-human-text)] hover:border-[var(--color-needs-human-text)]"
                            style={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length >= PAGE_LIMIT && (
              <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>Showing the first {PAGE_LIMIT} Clerk users.</p>
            )}
            {unbound > 0 && (
              <p className="cs-meta" data-testid="seats-unbound" style={{ color: 'var(--color-text-muted)' }}>
                {unbound} backend user row(s) have no Clerk id (D9 cleanup is owner-only via the API).
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
